import type { Collection } from "@tanstack/db";
import type { PlayerResponse } from "@softmax/observatory-client/types";
import {
  createPlayer,
  deletePlayerAvatar,
  setDefaultPlayer,
  uploadPlayerAvatar,
} from "./player-api";

export type PlayerAction =
  | {
      kind: "create";
      name: string;
    }
  | {
      kind: "rename";
      player: Pick<PlayerResponse, "id">;
      name: string;
    }
  | {
      kind: "default" | "remove-avatar";
      player: PlayerResponse;
    }
  | {
      kind: "upload-avatar";
      player: PlayerResponse;
      file: File;
    };

export async function performPlayerAction({
  action,
  source,
  options,
}: {
  readonly action: PlayerAction;
  readonly source: Pick<
    Collection<PlayerResponse, string>,
    "preload" | "update" | "get"
  >;
  readonly options: {
    readonly elevatedPrivileges: boolean;
    readonly signal: AbortSignal;
  };
}): Promise<PlayerResponse> {
  const { signal } = options;
  signal.throwIfAborted();

  if (action.kind === "create")
    return createPlayer({ name: action.name, options });

  switch (action.kind) {
    case "rename": {
      await source.preload();
      signal.throwIfAborted();
      await source
        .update(action.player.id, (player) => {
          player.name = action.name;
        })
        .when("settled");
      signal.throwIfAborted();
      const player = source.get(action.player.id);

      if (player === undefined)
        throw new Error(
          "The acknowledged player is missing from its collection",
        );

      return player;
    }
    case "default":
      return setDefaultPlayer({ playerId: action.player.id, options });
    case "remove-avatar": {
      await deletePlayerAvatar({ playerId: action.player.id, options });
      return { ...action.player, avatar_url: null };
    }
    case "upload-avatar": {
      const avatarUrl = await uploadPlayerAvatar({
        playerId: action.player.id,
        image: action.file,
        options,
      });
      return { ...action.player, avatar_url: avatarUrl };
    }
  }
}
